import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, NavigationHeader } from 'design-system';
import AmountSelectors from 'design-system/AmountSelectors';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import { OnboardingFlow } from 'utils/types/navigation';
import { savePotsOnboardingState } from '../actions';
import useNavigateToNextScreenForPotsOnboarding from '../hooks/useNavigateToNextScreenForPotsOnboarding';
const strings = {
    navTitle: 'Choose initial deposit',
    button: 'Continue',
};
const AMOUNT_SELECTORS = [50, 100, 250];
const DEFAULT_AMOUNT = 100;
const PotInitialDepositAmountScreen = ({ route, navigation }) => {
    const moneyInputRef = useRef(null);
    const amountRef = useRef(DEFAULT_AMOUNT);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const dispatch = useAppDispatch();
    const createPotsParams = useAppSelector((state) => state.pots.onboarding?.createPotsParams);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const navigateToNextStep = useNavigateToNextScreenForPotsOnboarding();
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('Choose Initial Deposit', { source: 'save' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
    }, []);
    const onSubmit = useCallback(async () => {
        if (createPotsParams) {
            dispatch(savePotsOnboardingState({ initialDepositAmount: amountRef.current }));
            navigateToNextStep();
        }
    }, [createPotsParams, navigateToNextStep]);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <AmountSelectors amounts={AMOUNT_SELECTORS} moneyInputRef={moneyInputRef}/>
      </View>), [styles.numPadHeader]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const outputColors = useMemo(() => [colors.background.dark, colors.background.dark], [colors.background.dark]);
    const scrollY = useSharedValue(0);
    const paddingBottomStyles = useMarginBottom('paddingBottom');
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight title={strings.navTitle} outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={mountedOnAnotherScreen ? styles.rightContent : null}>
            <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.POTS_DEPOSIT}/>
          </View>}/>
      <View style={[
            styles.container,
            paddingHorizontalStyle,
            { paddingBottom: mountedOnAnotherScreen ? rem(16) : paddingBottomStyles.paddingBottom },
        ]}>
        <StatusBar barStyle="dark-content" animated/>
        <MoneyInput flex max={1500} showMaxHint ref={moneyInputRef} colors={moneyInputColors} restrictAmountsLessThanOne numPadHeader={numPadHeader} alwaysDisplayMinHint={false} alwaysDisplayMaxHint={false} initialValue={DEFAULT_AMOUNT} onValueChange={onAmountChange} pinPadStyle={styles.pinPadStyle} inputFieldStyle={styles.inputFieldStyle}/>
        <Spacer h={20}/>
        <View style={styles.buttonContainer}>
          <Button brand title={strings.button} onPress={onSubmit}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
    inputFieldStyle: {
        fontSize: rem(64),
        lineHeight: rem(87.3),
    },
    pinPadStyle: {
        marginTop: rem(-10),
    },
    rightContent: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default PotInitialDepositAmountScreen;
